<template>
  <div v-if="!isAuthenticated" class="d-flex flex-column align-items-center justify-content-center min-vh-100">
    <div class="bg-white p-4 rounded shadow w-100" style="max-width: 400px;">
      <h2 class="text-center text-dark mb-4">Enter PIN to Access eNotes</h2>

      <input type="password" v-model="pinInput" 
        class="form-control mb-3" placeholder="Enter PIN" @keyup.enter="checkPin" />
      <button @click="checkPin" class="btn btn-primary w-100">
        Unlock
      </button>
    </div>
  </div>

  <div v-else class="container">
    <div class="d-flex justify-content-end mb-2">
      <button @click="logout" class="btn btn-danger w-100">Log Out</button>
    </div>

    <header class="text-center mb-4">
      <h1 class="display-4 text-primary">eNotes Fareez</h1>
      <p class="text-muted">Nota daripada format markdown</p>
    </header>

    <div class="row g-4">
      <div class="col-lg-4 col-md-6" v-if="isSidebarVisible">
        <button @click="toggleSidebar" class="btn btn-secondary mb-3">Hide Sidebar</button>
        <div class="card">
          <div class="card-body">
            <input type="text" v-model="searchQuery" class="form-control mb-3" placeholder="Search files...">
            <FileUpload @file-uploaded="handleFileUpload" />
            <FileList :files="filteredFiles" :selectedFileId="selectedFileId" @file-selected="selectFileToView"
              @delete-file="deleteFile" />
          </div>
        </div>
      </div>
      <div :class="isSidebarVisible ? 'col-lg-8 col-md-6' : 'col-12'">
        <button v-if="!isSidebarVisible" @click="toggleSidebar" class="btn btn-secondary mb-3">Show Sidebar</button>
        <div class="card h-100">
          <div class="card-body markdown-content">
            <MarkdownViewer :file="selectedFile" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import FileUpload from './components/FileUpload.vue'
import FileList from './components/FileList.vue'
import MarkdownViewer from './components/MarkdownViewer.vue'

const uploadedFiles = ref([])
const selectedFile = ref(null)
const isAuthenticated = ref(false)
const pinInput = ref('')
const isSidebarVisible = ref(true)
const searchQuery = ref("")

// dev guna yang ni
const AUTH_PIN = '5431'

const selectedFileId = computed(() => selectedFile.value?.id || null)

const filteredFiles = computed(() => {
  return uploadedFiles.value.filter(file =>
    file.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
})

// development guna yang ni
const checkPin = () => {


  if (pinInput.value === AUTH_PIN) {
    isAuthenticated.value = true
    localStorage.setItem('enotes_authenticated', 'yes')
    loadNotes()
  } else {
    alert('Incorrect PIN')
  }
}

// const checkPin = async () => {
//   try {
//     const res = await fetch('https://api.fare4z.com/enotes/validate-pin', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ pin: pinInput.value })
//     });

//     const data = await res.json();

//     if (data.valid) {
//       isAuthenticated.value = true;
//       localStorage.setItem('enotes_authenticated', 'yes');
//       loadNotes();
//     } else {
//       alert('Incorrect PIN');
//     }
//   } catch (error) {
//     console.error('PIN validation error:', error);
//     alert('Connection failed. Please check the network or backend.');
//   }
// };


function handleFileUpload(file) {
  uploadedFiles.value.unshift(file)
  selectedFile.value = file
}

function selectFileToView(file) {
  selectedFile.value = file
}

function deleteFile(fileId) {
  uploadedFiles.value = uploadedFiles.value.filter(f => f.id !== fileId)


  // delete the file from the server
  fetch(`https://api.fare4z.com/enotes/delete/${fileId}`, {
    method: 'DELETE'
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
    })
    .catch(error => {
      console.error('Error deleting file:', error);
      alert('Failed to delete file. Please try again.');
    });

}

async function loadNotes() {
  const res = await fetch('https://api.fare4z.com/enotes/notes')
  const data = await res.json()
  uploadedFiles.value = data
  selectedFile.value = data[0]
}

const logout = () => {
  localStorage.removeItem('enotes_authenticated');
  isAuthenticated.value = false;
};

const toggleSidebar = () => {
  isSidebarVisible.value = !isSidebarVisible.value;
};


onMounted(() => {
  if (localStorage.getItem('enotes_authenticated') === 'yes') {
    isAuthenticated.value = true
    loadNotes()
  }
})
</script>

<style>
body {
  font-family: 'Inter', sans-serif;
}

/* Beautify markdown content */
.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  font-weight: 600;
  margin-top: 1.25em;
  margin-bottom: 0.5em;
  line-height: 1.25;
}

.markdown-content h1 {
  font-size: 2.5rem;
  color: #0d6efd;
}

.markdown-content h2 {
  font-size: 2rem;
  color: #0d6efd;
}

.markdown-content h3 {
  font-size: 1.75rem;
  color: #0d6efd;
}

.markdown-content p {
  margin-bottom: 1em;
  line-height: 1.6;
  font-size: 1.125rem;
}

.markdown-content ul,
.markdown-content ol {
  margin-bottom: 1em;
  padding-left: 1.5em;
}

.markdown-content ul {
  list-style-type: disc;
}

.markdown-content ol {
  list-style-type: decimal;
}

.markdown-content a {
  color: #0d6efd;
  text-decoration: underline;
}

.markdown-content a:hover {
  color: #0a58ca;
}

.markdown-content pre {
  background-color: #f8f9fa;
  padding: 1em;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin-bottom: 1em;
  font-size: 0.875rem;
}

.markdown-content code {
  font-family: monospace;
  background-color: #e9ecef;
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
  font-size: 0.9em;
}

.markdown-content blockquote {
  border-left: 4px solid #ced4da;
  padding-left: 1em;
  margin-left: 0;
  margin-bottom: 1em;
  color: #6c757d;
  font-style: italic;
}
</style>